import type { HitlReason, PipelineItem } from "@/types/tina";

interface CoalesceCopy {
  /** The shared entity these grouped HITLs are about: "venue" | "movie". */
  entity: string;
  /** Trailing phrase for the resolve banner, e.g. "at this venue" / "for this movie". */
  bannerSuffix: string;
}

/**
 * Curator-facing copy for a coalesced HITL cluster. Only venue and movie reasons
 * coalesce — the backend stamps `hitl_dedup_key` for exactly those two families
 * (see Tina's hitl_coalesce reason-family scoping) — so every other reason returns
 * null and shows no cluster affordance.
 */
export function coalesceCopy(
  reason: HitlReason | null | undefined,
): CoalesceCopy | null {
  if (!reason) return null;
  if (reason.startsWith("venue_"))
    return { entity: "venue", bannerSuffix: "at this venue" };
  if (reason.startsWith("movie_"))
    return { entity: "movie", bannerSuffix: "for this movie" };
  return null;
}

/**
 * Amber banner telling the curator that resolving this representative also clears
 * the N same-key HITLs coalesced behind it. Renders nothing when there are no
 * coalesced siblings or the reason does not coalesce. Shared by the venue and
 * movie resolution panels so both read identically.
 */
export function CoalescedResolveBanner({ item }: { item: PipelineItem }) {
  const count = item.coalesced_count ?? 0;
  const copy = coalesceCopy(item.hitl_reason);
  if (count <= 0 || copy === null) return null;
  return (
    <p className="rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-200">
      Resolving this also clears {count} more event{count === 1 ? "" : "s"}{" "}
      {copy.bannerSuffix}.
    </p>
  );
}
